import { useState, useCallback, useRef } from 'react';
import {
  extractDocument,
  extractFields,
  validateFields,
} from '../services/documentExtraction.service';

export function useDocumentExtraction() {
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const cachedOcrRef = useRef(null);

  const extract = useCallback(async (file, hintedType = null) => {
    setStatus('preprocessing');
    setProgress(0);
    setError(null);
    setResult(null);
    cachedOcrRef.current = null;

    try {
      const res = await extractDocument(file, hintedType, (s, p) => {
        setStatus(s);
        setProgress(p);
      });
      cachedOcrRef.current = { text: res.rawOcrText, confidence: res.confidence };
      setResult(res);
      setStatus('done');
      setProgress(100);
    } catch (err) {
      setError(err.message || 'Extraction failed');
      setStatus('error');
    }
  }, []);

  const reExtractFromText = useCallback((newType) => {
    if (!cachedOcrRef.current) return;
    const { text } = cachedOcrRef.current;
    const { fields } = extractFields(text, newType);
    // Convert to plain values for validateFields
    const plainFields = Object.fromEntries(
      Object.entries(fields).map(([k, v]) => [k, v?.value || ''])
    );
    const { valid, warnings } = validateFields(plainFields, newType);
    const extractedCount = Object.values(fields).filter((f) => f && f.value).length;
    const totalFields = Object.keys(fields).length;
    setResult((prev) => ({
      ...prev,
      documentType: newType,
      extractedFields: fields,
      validationStatus: {
        valid,
        warnings,
        checksumValid: valid,
        allRequiredFieldsExtracted: extractedCount > 0 && extractedCount >= Math.min(2, totalFields),
      },
    }));
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setResult(null);
    setError(null);
    setProgress(0);
    cachedOcrRef.current = null;
  }, []);

  return { extract, reExtractFromText, reset, status, result, error, progress };
}
